'use client';
import { useEffect, useState } from 'react';
import type { MouseEvent } from 'react';
import CloseIcon from '@repo/ui/src/components/Icons/CloseIcon';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import FilterAccordion from './_FilterAccordion';
import { classNames } from '@repo/ui/src/utils/classNames';

interface FilterOption {
  title: string;
  filter: string;
  count: number;
}

interface FilterGroup {
  title: string;
  filterType: string;
  options: Array<FilterOption>;
}

interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  filters?: Array<FilterGroup>;
  currentFilters: Record<string, Array<string>>;
  setFilters: (selection: { filterType: string; filterName: string }) => void;
  clearFilters: () => void;
}

const FilterDrawer = ({
  open,
  onClose,
  filters,
  currentFilters,
  setFilters,
  clearFilters,
}: FilterDrawerProps) => {
  const { t } = useTranslation();
  const [isAnimating, setIsAnimating] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setIsVisible(true);

      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;

      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollBarWidth}px`;

      const timer = setTimeout(() => setIsAnimating(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);

        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  if (!isVisible) return null;

  const headerId = 'filter-drawer-heading';

  const handleBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className={classNames(
        'fixed inset-0 z-modal overflow-hidden transition-opacity duration-300 bg-black bg-opacity-50',
        isAnimating ? 'opacity-100' : 'opacity-0',
      )}
      onClick={handleBackdropClick}
    >
      <div
        className={classNames(
          'fixed left-0 top-0 h-full px-2 bg-white shadow-lg flex flex-col min-w-[300px]',
          'transition-transform duration-300 ease-in-out overflow-y-auto',
          isAnimating ? 'translate-x-0' : 'translate-x-[-100%]',
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headerId}
        onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
      >
        <div className="flex justify-end pb-2">
          <button
            data-dismiss="modal"
            aria-label={t('common.close', undefined, { default: 'Close' })}
            onClick={onClose}
            className="p-2"
          >
            <CloseIcon />
          </button>
        </div>
        <div className="px-6 pt-12 pb-1">
          <div className="flex justify-between items-center mb-2">
            <h2 id={headerId} className="tra-text-web-heading-5 mb-4">
              {t('recipeCategory.filters', undefined, { default: 'Filters' })}
            </h2>
            <Button
              onClick={() => clearFilters()}
              variant={ButtonVariant.bare}
              size="small"
              className="text-brand-400"
            >
              {t('recipeCategory.clearAll', undefined, { default: 'Clear All' })}
            </Button>
          </div>
          {filters?.map((filter, i) => (
            <FilterAccordion
              currentFilters={currentFilters}
              setFilters={setFilters}
              key={`filter_accordion_${i}`}
              filter={filter}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default FilterDrawer;
